import type { CatalogueCurrency } from '~/lib/catalogue-price'
import { logNonFatal } from '~/lib/diagnostics'
import { api } from './api'
import {
  errorDetail,
  type ShopBootstrap,
  type ShopCollectionProduct,
  type ShopProductCollection,
} from './bootstrap'
import { compareByDisplayOrder } from './collection-order'

// The refresh tickets (GLOSSARY.md "Refresh ticket (刷新券)"): the
// shop-daily-refresh products that each buy one Daily Picks refresh, in
// refresh-tier order — the collection's display_order rule, positional
// (GLOSSARY.md "Refresh tier"). A ticket carries no sale, so its price is the
// plain price. Its item type is shared across the collection and names the
// refresh count's purchase stats.

export interface RefreshTicket {
  currency: CatalogueCurrency
  itemTypeId: number
  price: number
  productId: number
}

type RefreshTicketOmissionReason =
  | 'invalid_collection_entry'
  | 'invalid_item_type'
  | 'invalid_price'
  | 'invalid_purchase_type'
  | 'missing_product'
  | 'not_purchaseable'

type RefreshTicketsUnavailableReason =
  | 'empty_collection'
  | 'missing_collection'
  | 'no_valid_ticket'

function reportOmission(
  reason: RefreshTicketOmissionReason,
  productId: number | undefined,
): void {
  logNonFatal('shop_refresh_ticket_omitted', {
    reason,
    ...(productId === undefined ? {} : { product_id: String(productId) }),
  })
}

function reportUnavailable(reason: RefreshTicketsUnavailableReason): void {
  logNonFatal('shop_refresh_tickets_unavailable', { reason })
}

function isPositiveInteger(value: unknown): value is number {
  return Number.isInteger(value) && Number(value) > 0
}

function deriveTicket(entry: ShopCollectionProduct): RefreshTicket | null {
  const product = entry.item_type_product
  if (!product) {
    reportOmission('missing_product', entry.item_type_product_id)
    return null
  }
  const itemTypeId = product.item_type_id
  if (!isPositiveInteger(itemTypeId)) {
    reportOmission('invalid_item_type', product.id)
    return null
  }
  if (product.purchase_type !== 'coin' && product.purchase_type !== 'gem') {
    reportOmission('invalid_purchase_type', product.id)
    return null
  }
  if (!Number.isFinite(product.price) || product.price <= 0) {
    reportOmission('invalid_price', product.id)
    return null
  }
  if (product.purchaseable !== true) {
    reportOmission('not_purchaseable', product.id)
    return null
  }
  return {
    currency: product.purchase_type,
    itemTypeId,
    price: product.price,
    productId: product.id,
  }
}

// The valid tickets in tier order. Missing, empty, or all-invalid yields an
// empty list — the button hides — with the reason reported; never a failed
// bootstrap.
export function deriveRefreshTickets(
  collection: ShopProductCollection | undefined,
): RefreshTicket[] {
  if (!collection) {
    reportUnavailable('missing_collection')
    return []
  }
  const entries = (
    Array.isArray(collection.item_type_product_collection_products)
      ? collection.item_type_product_collection_products
      : []
  ).filter((entry) => {
    if (entry && typeof entry === 'object') return true
    reportOmission('invalid_collection_entry', undefined)
    return false
  })
  if (entries.length === 0) {
    reportUnavailable('empty_collection')
    return []
  }
  const tickets = entries.sort(compareByDisplayOrder).flatMap((entry) => {
    const ticket = deriveTicket(entry)
    return ticket ? [ticket] : []
  })
  if (tickets.length === 0) reportUnavailable('no_valid_ticket')
  return tickets
}

// The refresh tier (GLOSSARY.md): the k-th refresh of the day, k = count + 1,
// buys the ticket at position min(k, N); the last ticket repeats for every
// later refresh. Positional — the display_order value is never matched.
export function resolveRefreshTier(
  tickets: readonly RefreshTicket[],
  count: number,
): RefreshTicket | undefined {
  if (tickets.length === 0) return undefined
  const k = Math.max(0, Math.floor(count)) + 1
  return tickets[Math.min(k, tickets.length) - 1]
}

// The item-type own-purchase stats (confirmed with the backend, #408):
// per-currency rows under total / today / month. The refresh count is
// today's total quantity across currencies — every ticket counts, whatever
// it cost (GLOSSARY.md "Refresh count"), so the read asks for today's range
// only and still reads the today list. A missing or empty today list is a
// day with no refreshes yet.
interface OwnPurchaseStatsRow {
  purchase_type?: unknown
  total_price?: unknown
  total_quantity?: unknown
}

interface ItemTypeOwnPurchaseStatsResponse {
  month?: OwnPurchaseStatsRow[] | null
  today?: OwnPurchaseStatsRow[] | null
  total?: OwnPurchaseStatsRow[] | null
}

function sumQuantities(rows: OwnPurchaseStatsRow[] | null | undefined): number {
  if (!Array.isArray(rows)) return 0
  let sum = 0
  for (const row of rows) {
    const quantity = row?.total_quantity
    if (
      typeof quantity === 'number' &&
      Number.isFinite(quantity) &&
      quantity > 0
    ) {
      sum += Math.floor(quantity)
    }
  }
  return sum
}

async function loadRefreshCount(
  userId: number,
  itemTypeId: number,
): Promise<number> {
  try {
    const response = await api.service
      .get(`store/users/${userId}/item-type/${itemTypeId}/own-purchase-stats`, {
        searchParams: { range: 'today' },
      })
      .json<ItemTypeOwnPurchaseStatsResponse>()
    return sumQuantities(response.today)
  } catch (error) {
    throw new Error(errorDetail(error))
  }
}

// The refresh count's one read per page load (GLOSSARY.md "Refresh count"),
// off the bootstrap critical path like trial eligibility (ADR 0023): the
// button shows a skeleton while it resolves and hides on a terminal failure,
// so retrying costs nothing visible. Keyed by the tickets' shared item type,
// which the first ticket carries; there is nothing to read until bootstrap
// has landed and a valid ticket exists.
export const refreshCountQuery = (
  bootstrap: Pick<ShopBootstrap, 'currentUser'> | undefined,
  firstTicket: RefreshTicket | undefined,
) => ({
  queryKey: ['shop', 'refresh-count', firstTicket?.itemTypeId] as const,
  queryFn: () =>
    loadRefreshCount(bootstrap!.currentUser.id, firstTicket!.itemTypeId),
  enabled: bootstrap !== undefined && firstTicket !== undefined,
  gcTime: Infinity,
  retry: 2,
  staleTime: Infinity,
})
